import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { listAITrainings } from '../../../../graphql/queries';

interface AITraining {
  id: string;
  orgID: string;
  service: string;
  name: string;
  description?: string;
  prompt: string;
  revision: number;
  status?: string;
  users?: string[];
  public?: boolean;
  createdBy?: string;
  lastUpdatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface ListAITrainingsResponse {
  listAITrainings: {
    items: AITraining[];
  };
}

export default async function fetchAIPrompts(
  orgID: string
): Promise<AITraining[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listAITrainings, {
        filter: {
          orgID: { eq: orgID },
        },
      })
    )) as GraphQLResult<ListAITrainingsResponse>;

    if (result.data) {
      // Get the production version of each service, fallback to latest if no production
      const serviceMap = new Map();
      const productionPrompts = new Map();
      const latestPrompts = new Map();

      result.data.listAITrainings.items.forEach((item: AITraining) => {
        // Track production prompts
        if (item.status === 'production') {
          productionPrompts.set(item.service, item);
        }

        // Track latest prompts as fallback
        const current = latestPrompts.get(item.service);
        if (!current || item.revision > current.revision) {
          latestPrompts.set(item.service, item);
        }
      });

      // For each service, prefer production prompt, fallback to latest
      const services = new Set([
        ...Array.from(productionPrompts.keys()),
        ...Array.from(latestPrompts.keys()),
      ]);
      services.forEach((service) => {
        const productionPrompt = productionPrompts.get(service);
        const latestPrompt = latestPrompts.get(service);
        serviceMap.set(service, productionPrompt || latestPrompt);
      });

      return Array.from(serviceMap.values());
    }
    return [];
  } catch (error) {
    console.error('Error fetching AI prompts:', error);
    return [];
  }
}
